import { css } from "@emotion/react";
import PropTypes from "prop-types";
import {useRef, useEffect} from "react";

const Video = ({ video_provider, video_id, autoplay, unmute, background, onVideoEnd, activate, paused, onVideoLoad }) => {

    const rawVideoRef = useRef();

    /** if the parent wants to watch for video end **/

    useEffect(()=>{

        if(onVideoEnd){

            let rawRefValue = null

            if(rawVideoRef.current){
                rawVideoRef.current.addEventListener('ended',onVideoEnd,false);
                rawRefValue = rawVideoRef.current
            }

            /** TODO, should the active change also remove this event listner? **/

            return ()=> {
                if(rawRefValue) rawRefValue.removeEventListener('ended',onVideoEnd,false);
            }

        }

    }, [onVideoEnd])

    useEffect(()=>{

        if(onVideoLoad){

            let rawRefValue = null

            const calcDims = (e) => {
                onVideoLoad(e.target.videoWidth,e.target.videoHeight)
            }

            if(rawVideoRef.current){
                rawVideoRef.current.addEventListener('loadedmetadata',calcDims,false);
                rawRefValue = rawVideoRef.current
            }

            /** TODO, should the active change also remove this event listner? **/

            return ()=> {
                if(rawRefValue) rawRefValue.removeEventListener('ended',calcDims,false);
            }

        }

    }, [onVideoLoad])

    /** if the parent needs to restart a video **/
    useEffect(()=>{
        if(rawVideoRef.current){

            if(activate){
                rawVideoRef.current.currentTime = 0;
                rawVideoRef.current.play();
            } else {
                rawVideoRef.current.pause();
            }
        }
    }, [activate])

    useEffect(()=>{
        if(rawVideoRef.current){
            if(paused){
                rawVideoRef.current.pause();
            } else {
                rawVideoRef.current.play();
            }
        }
    }, [paused])

    const frameCss = css(`
        position: absolute;
        top:0;
        left:0;
        ${!background ? 'width:100%;' : ''}
        height:100%;
        background-color:black;
        ${background ? 'object-fit: cover;' : ''}
    `);

    const ekoEmbed = id => {
        const attributes = {
            height: "100%",
            src: `https://video.eko.com/v/${id}/embed?autoplay=${autoplay ? "true" : "false"}`,
            allow: "autoplay; fullscreen"
        };

        if(!background) attributes.width = '100%';

        return <iframe css={frameCss} title={`${video_provider}/${id}`} {...attributes} ></iframe>
    };

    const youtubeEmbed = id => {

        const urlArgs = {
            modestbranding: 1,
            rel: 0,
            "iv_load_policy": 3,
            showsearch: 0,
            autohide: 0,
        }

        // by coincidence this is pretty similar to the vimeo autoplay method, i suggest keeping this broken out to not kneecap any new video provider's ability to autoplay.
        if (autoplay) {
            urlArgs.autoplay = 1;
            urlArgs.controls = 1;
            urlArgs.showinfo = 0;
        }

        if(autoplay && !unmute){
            urlArgs.mute = 1;
            urlArgs.playsinline = 1;
        }

        // Turn our k=>v object into a nice http GET formatted string.
        const finalArgs = Object.keys(urlArgs).map(i => `${i}=${urlArgs[i]}`).join("&");

        const attributes = {
            height: "100%",
            src: `https://www.youtube.com/embed/${id}?${finalArgs}`,
            frameBorder: 0,
            allow: "autoplay; fullscreen"
        };

        if(!background) attributes.width = '100%';

        return <iframe css={frameCss} title={`${video_provider}/${id}`} {...attributes} ></iframe>
    };

    const vimeoEmbed = id => {

        const urlArgs = {
            color: "ffffff"
        }

        // by coincidence this is pretty similar to the youtube autoplay method, i suggest keeping this broken out to not kneecap any new video provider's ability to autoplay.
        if (autoplay) {
            urlArgs.autoplay = 1;
            urlArgs.controls = 0;
            urlArgs.showinfo = 0;
        }

        if(autoplay && !unmute){
            urlArgs.muted = 1;
        }

        if(background){
            urlArgs.background = 1
        }

        // Turn our k=>v object into a nice http GET formatted string.
        const finalArgs = Object.keys(urlArgs).map(i => `${i}=${urlArgs[i]}`).join("&");

        const attributes = {
            height: "100%",
            src: `https://player.vimeo.com/video/${id}?${finalArgs}`,
            frameBorder: 0,
            allow: "autoplay; fullscreen"
        };

        if(!background) attributes.width = '100%';

        return <iframe css={frameCss} title={`${video_provider}/${id}`} {...attributes} ></iframe>
    };

    const vzaarEmbed = id => {
        const attributes = {
            height: "100%",
            src: `https://view.vzaar.com/${id}/player?GAOn=${autoplay ? "true" : "false"}`,
            frameBorder: 0,
            allow: "autoplay; fullscreen"
        };

        if(!background) attributes.width = '100%';

        return <iframe css={frameCss} title={`${video_provider}/${id}`} {...attributes} ></iframe>
    };

    const getRawEmbedCode = id => {

        const attributes = {
            width: "100%",
            height: 'auto',
            muted: !unmute,
            autoPlay: autoplay,
            // loop: autoplay,
            controls: !background,
            playsInline: true
        }

        /* eslint-disable jsx-a11y/media-has-caption */
        return (
            <video {...attributes} ref={rawVideoRef}>
                <source src={id} type="video/mp4"/>
            </video>
        )
    }

    const showEmbed = (video_provider, video_id) => {
        const embedMap = {
            eko: ekoEmbed,
            vimeo: vimeoEmbed,
            youtube: youtubeEmbed,
            vzaar: vzaarEmbed,
            raw_url: getRawEmbedCode
        };
        if (embedMap[video_provider]) return embedMap[video_provider](video_id);
        console.warn(`No Video Provider for ${video_provider}/${video_id}`)
        return null
    }

    const aspectRatioWrap = () => {
        if(!background){
            return css(`
            padding-bottom: 56.25%; // 16:9
            width:100%;
            height:auto;
            position: relative;
        `);
        }
    }

    const centerVertically = css(`
        height: 100%;
        width: 100%;
        align-items: center;
        display: flex;
        justify-content: center;
    `)

    return <div css={[aspectRatioWrap, centerVertically]}>
        {showEmbed(video_provider, video_id)}
        {/*{JSON.stringify([video_provider, video_id], false, '\t')}*/}
    </div>
};

Video.propTypes = {
    video_provider: PropTypes.string,
    video_id: PropTypes.string,
    autoplay: PropTypes.bool,
    unmute: PropTypes.bool,
    background: PropTypes.bool,
    onVideoEnd: PropTypes.func,
    activate: PropTypes.bool,
    paused: PropTypes.bool,
    onVideoLoad: PropTypes.func,
};


export default Video;
